import {
    createContext, useCallback, useContext, useEffect, useState
} from 'react';
import {
    AppLoader
} from 'shared/components/LoaderSpinner';
import {
    apiKeyService,
    organizationService, subscriptionService
} from 'shared/api';
import {
    useParams
} from 'react-router-dom';
import {
    Context
} from './GlobalState';
import type {
    AugmentedRoleChecker,
    Organization,
    OrganizationContextValue,
    OrganizationProviderProps,
    OrganizationSubscription,
    SelectedOrganization,
    SubOrganizationMinimal
} from './context.types';

const defaultOrganizationContext: OrganizationContextValue = {
    actions: () => {
        // Default empty action handler
    },
    loading: true,
    selectedOrganization: null,
    subscription: null,
    getUserRole: () => Object.assign(() => '', {
        isSuperAdmin: false,
        isAdmin: false,
        isEditor: false
    }),
    refreshSubOrganizations: async () => [],
};

export const OrganizationContext = createContext<OrganizationContextValue>(defaultOrganizationContext);

export const OrganizationProvider = ({
    children
}: OrganizationProviderProps) => {

    const [selectedOrganization, setSelectedOrganization] = useState<SelectedOrganization | null>(null);
    const [subscription, setSubscription] = useState<OrganizationSubscription | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const {
        oragID
    } = useParams();
    const {
        actions: globalAction, organizations
    } = useContext(Context);

    const getOrgData = async () => {
        setLoading(true);
        setSubscription(null);
        try {
            // fetch sub organizations if not cached in organizations state
            let subOrganizations: SubOrganizationMinimal[] = [];
            if (organizations.find((org: Organization) => org._id === oragID)?.subOrganizations) {
                subOrganizations = (organizations.find((org: Organization) => org._id === oragID)?.subOrganizations) || [];
            } else {
                const results = await organizationService.getSubOrganizations(oragID);
                globalAction({
                    type: 'SET_ORGANIZATIONS',
                    payload: (prev: Organization[]) => prev.map((org: Organization) => (
                        org._id === oragID ? {
                            ...org,
                            subOrganizations: results.data as SubOrganizationMinimal[]
                        } : org
                    ))
                });
                subOrganizations = results.data;
            }

            const byoKeys = await apiKeyService.listApiKeys('organization', oragID);

            setSelectedOrganization(prev => ({
                ...prev,
                subOrganizations: subOrganizations,
                byoKeys: byoKeys.data
            }));

            const subscriptionResponse = await subscriptionService.getOrganizationSubscription(oragID);
            setSubscription(subscriptionResponse.data);
        } catch { /* empty */

        } finally {
            setLoading(false);
        }
    };

    const refreshSubOrganizations = useCallback(async (): Promise<SubOrganizationMinimal[]> => {
        if (!oragID) return [];
        const results = await organizationService.getSubOrganizations(oragID);
        const subOrganizations = results.data as SubOrganizationMinimal[];
        globalAction({
            type: 'SET_ORGANIZATIONS',
            payload: (prev: Organization[]) => prev.map((org: Organization) => (
                org._id === oragID ? {
                    ...org,
                    subOrganizations,
                } : org
            )),
        });
        setSelectedOrganization((prev) => (prev ? {
            ...prev,
            subOrganizations,
        } : prev));
        return subOrganizations;
    }, [oragID, globalAction]);

    useEffect(() => {
        getOrgData();
    }, [oragID]);

    // Actions
    const actions = (action: { type: 'SET_LOADING'; payload: boolean } | { type: 'SET_SELECTED_ORGANIZATION'; payload: SelectedOrganization | null | ((prev: SelectedOrganization | null) => SelectedOrganization | null) }) => {
        const {
            type, payload
        } = action;

        switch (type) {
            case 'SET_LOADING':
                return setLoading(payload);
            case 'SET_SELECTED_ORGANIZATION':
                return setSelectedOrganization(payload);
            default:
                return loading;
        }
    };

    function getUserRole(): AugmentedRoleChecker {

        function roleChecker(role: string) {
            return selectedOrganization ? selectedOrganization.userRole === role : false;
        }

        return Object.assign(() => (selectedOrganization ? selectedOrganization.userRole : ''), {
            isSuperAdmin: roleChecker('superAdmin'),
            isAdmin: roleChecker('admin'),
            isEditor: roleChecker('editor'),
        });
    }

    if (loading) {
        return <div className='flex flex-grow flex-col justify-center items-center'> <AppLoader size={50} /> </div>;
    }

    return (
        <OrganizationContext.Provider
            value={{
                actions,
                loading,
                selectedOrganization,
                subscription,
                getUserRole,
                refreshSubOrganizations,
            }}
        >
            {children}
        </OrganizationContext.Provider>
    );
};
